import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ChevronLeft,
  Info,
  ArrowLeftRight,
  CheckCircle2,
  XCircle,
  Logs,
} from "lucide-react";
import Toast from "../../common/Toast/Toast";
import CfLoader from "../../common/CfLoader/CfLoader";
import EntityConstants from "../../configs/EntityConstants";
import VerificationApprovalModal from "../../common/VerificationApprovalModal/VerificationApprovalModal";
import VerificationApprovalCertificateModal from "../../common/VerificationApprovalCertificateModal/VerificationApprovalCertificateModal";
import DynamicVerificationApprovalModal from "../../common/DynamicVerificationApprovalModal/DynamicVerificationApprovalModal";
import { POST, GET } from "../../service/CRUDService";
import Lottie from "lottie-react";
import verificationAnimation from "../../images/verification.json";
import AlertIcon from "../../resources/icons/Alert.svg";
import LegacyMatchesModal from "../../common/LegacyMatchesModal/LegacyMatchesModal";
import { detectFileType } from "../../utils/DetectFileType";
import { formatDate } from "../../utils/formatDate";
import { getStablePdfUrl } from "../../utils/pdfUtils/stablePdfUrl";
import PdfPreview from "../../common/PdfPreview/PdfPreview";
import {
  formStructureBuilder,
  formatFieldName,
} from "../../utils/formStructureBuilder";
import { useTranslation } from "react-i18next";
import SideDrawer from "../../common/SideDrawer/SideDrawer";
import { fetchOrganisationsAPI } from "../../common/Organisations/fetchOrganisations";
import { fetchCurrentUser } from "../../service/CurrentUser";
import Dropdown from "../../common/Dropdown/Dropdown";
import SmallCFLoader from "../../common/SmallCFLoader/SmallCFLoader";
import AuthContext from "../../store/auth-context";
import { resolveVerificationApprovalStatus } from "../../utils/verificationApprovalStatus";

const LLM_POLLING_INTERVAL = 3000;

const TransactionDetailsPage = () => {
  const { transaction_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const authContext = React.useContext(AuthContext);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDynamicModalOpen, setIsDynamicModalOpen] = useState(false);
  const [actionType, setActionType] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [currentTransactionId, setCurrentTransactionId] = useState(null);
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [issuerComments, setIssuerComments] = useState("");
  const [verifierComments, setVerifierComments] = useState("");
  const [isCertificateModalOpen, setIsCertificateModalOpen] = useState(false);
  const [matchingVCs, setMatchingVCs] = useState([]);
  const [isMatchesModalOpen, setIsMatchesModalOpen] = useState(false);
  const [isMatchingVCsLoading, setIsMatchingVCsLoading] = useState(false);
  const [verificationReport, setVerificationReport] = useState(null);
  const [dynamicModalData, setDynamicModalData] = useState(null);
  const [isFetchingApiData, setIsFetchingApiData] = useState(false);
  const [isLlmPolling, setIsLlmPolling] = useState(false);
  const [isTransferDrawerOpen, setIsTransferDrawerOpen] = useState(false);
  const [tenantCode, setTenantCode] = useState("");
  const [currentUserOrgCode, setCurrentUserOrgCode] = useState("");
  const [transferOrgOptions, setTransferOrgOptions] = useState([]);
  const [selectedTransferOrg, setSelectedTransferOrg] = useState(null);
  const [isTransferSubmitting, setIsTransferSubmitting] = useState(false);
  const [isTransferOrgsLoading, setIsTransferOrgsLoading] = useState(false);
  const [programmeOptions, setProgrammeOptions] = useState([]);
  const [selectedProgramme, setSelectedProgramme] = useState(null);
  const [isProgrammesLoading, setIsProgrammesLoading] = useState(false);
  const [transferResult, setTransferResult] = useState(null);
  const [isTransferred, setIsTransferred] = useState(false);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const llmPollingIntervalRef = useRef(null);

  const verificationDataStructure = useMemo(() => {
    if (transactionDetails?.verification_data) {
      return formStructureBuilder(transactionDetails.verification_data);
    }
    return {};
  }, [transactionDetails?.verification_data]);

  const isTransferEnabled = useMemo(() => {
    return authContext.isTransferEnabled === true;
  }, [authContext.isTransferEnabled]);

  const renderSimpleFields = (structure, labelMap = {}) => {
    const simpleFields = Object.entries(structure).filter(([key, config]) => {
      if (
        key === "batch_name" &&
        (config.value === "" ||
          config.value === null ||
          config.value === undefined)
      ) {
        return false;
      }
      return config.type === "text" || config.type === "null";
    });

    return simpleFields.map(([key, config]) => (
      <div key={key} className="space-y-2">
        <span className="text-sm text-gray-500 block font-medium">
          {labelMap[key] || formatFieldName(key)}
        </span>
        <p className="text-gray-900 text-sm font-medium">
          {config.value || t("verificationApprovals:na")}
        </p>
      </div>
    ));
  };

  const renderComplexFields = (structure, labelMap = {}) => {
    const complexFields = Object.entries(structure).filter(
      ([, config]) =>
        config.type === "arrayOfObjects" ||
        config.type === "simpleArray" ||
        config.type === "object",
    );

    return complexFields.map(([key, config]) => {
      const label = labelMap[key] || formatFieldName(key);

      if (config.type === "arrayOfObjects" && config.isArray) {
        return (
          <div key={key} className="col-span-2 space-y-3">
            <span className="text-sm text-gray-500 block font-medium">
              {label}
            </span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto">
              {config.items.map((item, itemIndex) => (
                <div
                  key={itemIndex}
                  className="p-3 border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="space-y-2">
                    {config.fields.map((field) => (
                      <div key={field} className="flex justify-between">
                        <span className="text-xs text-gray-600 font-medium capitalize">
                          {formatFieldName(field)}:
                        </span>
                        <span className="text-xs text-gray-900 font-semibold text-right max-w-[60%] break-words">
                          {item[field] || t("verificationApprovals:na")}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      } else if (config.type === "simpleArray" && config.isArray) {
        return (
          <div key={key} className="col-span-2">
            <span className="text-sm text-gray-500 block font-medium">
              {label}
            </span>
            <div className="flex flex-wrap gap-2 mt-2">
              {config.items.map((item, itemIndex) => (
                <span
                  key={itemIndex}
                  className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        );
      } else if (config.type === "object" && !config.isArray) {
        return (
          <div key={key} className="col-span-2 space-y-3">
            <span className="text-sm text-gray-500 block font-medium">
              {label}
            </span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
              {Object.entries(config.value).map(([nestedKey, nestedValue]) => (
                <div
                  key={nestedKey}
                  className="flex justify-between items-start"
                >
                  <span className="text-xs text-gray-600 font-medium capitalize">
                    {formatFieldName(nestedKey)}:
                  </span>
                  <span className="text-xs text-gray-900 font-semibold text-right max-w-[60%] break-words">
                    {nestedValue || t("verificationApprovals:na")}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      }
      return null;
    });
  };

  const stopLlmPolling = () => {
    if (llmPollingIntervalRef.current) {
      clearInterval(llmPollingIntervalRef.current);
      llmPollingIntervalRef.current = null;
    }
    setIsLlmPolling(false);
  };

  const getLlmStatus = (data) =>
    data?.data?.llm_report_data?.status ||
    data?.data?.status ||
    data?.data?.llm_report_status ||
    data?.status;

  const isLlmInProgress = (status) =>
    status === "In-Progress" || status === "In Progress";

  const isLlmReportEmpty = (data) => {
    const report = data?.data?.llm_report_data;
    return !report || Object.keys(report).length === 0;
  };

  const startLlmPolling = () => {
    if (llmPollingIntervalRef.current) return;

    llmPollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await GET(
          `${EntityConstants.VERIFICATION_REPORT_DETAILS}/${transaction_id}`,
        );
        const data = await response.json();

        if (data?.success) {
          const llmStatus = getLlmStatus(data);
          const shouldContinue =
            isLlmInProgress(llmStatus) || isLlmReportEmpty(data);

          setDynamicModalData({
            llmReportData: data.data.llm_report_data || {},
            defaultFields: data.data.default_fields || [],
          });

          if (!shouldContinue) {
            stopLlmPolling();
          }
        }
      } catch (error) {
        stopLlmPolling();
      }
    }, LLM_POLLING_INTERVAL);
  };

  const handleAccept = async () => {
    setActionType("Approved");
    setIssuerComments("");

    const isLegacyType = ["Legacy", "Data", "OCR"].includes(
      transactionDetails?.document_type,
    );

    if (isLegacyType) {
      if (matchingVCs.length === 0) {
        setIsFetchingApiData(true);
        try {
          const response = await GET(
            `${EntityConstants.VERIFICATION_REPORT_DETAILS}/${transaction_id}`,
          );
          const data = await response.json();

          if (data.success) {
            const llmStatus = getLlmStatus(data);
            const shouldPoll =
              isLlmInProgress(llmStatus) || isLlmReportEmpty(data);

            setDynamicModalData({
              llmReportData: data.data.llm_report_data || {},
              defaultFields: data.data.default_fields || [],
            });
            setIsDynamicModalOpen(true);

            if (shouldPoll) {
              setIsLlmPolling(true);
              startLlmPolling();
            } else {
              stopLlmPolling();
            }
          } else {
            Toast({
              type: "error",
              title: t("verificationApprovals:error"),
              message:
                data.message ||
                t("verificationApprovals:failed_to_fetch_details"),
            });
          }
        } catch (error) {
          Toast({
            type: "error",
            title: t("verificationApprovals:error"),
            message: t("verificationApprovals:failed_to_fetch_details"),
          });
        } finally {
          setIsFetchingApiData(false);
        }
      } else {
        setIsModalOpen(true);
      }
    } else {
      setIsModalOpen(true);
    }
  };

  const handleReject = async () => {
    setActionType("Rejected");
    setIssuerComments("");

    const isLegacyType = ["Legacy", "Data", "OCR"].includes(
      transactionDetails?.document_type,
    );

    if (isLegacyType) {
      if (matchingVCs.length === 0) {
        setIsFetchingApiData(true);
        try {
          const response = await GET(
            `${EntityConstants.VERIFICATION_REPORT_DETAILS}/${transaction_id}`,
          );
          const data = await response.json();

          if (data.success) {
            const llmStatus = getLlmStatus(data);
            const shouldPoll =
              isLlmInProgress(llmStatus) || isLlmReportEmpty(data);

            setDynamicModalData({
              llmReportData: data.data.llm_report_data || {},
              defaultFields: data.data.default_fields || [],
            });
            setIsDynamicModalOpen(true);

            if (shouldPoll) {
              setIsLlmPolling(true);
              startLlmPolling();
            } else {
              stopLlmPolling();
            }
          } else {
            Toast({
              type: "error",
              title: t("verificationApprovals:error"),
              message:
                data.message ||
                t("verificationApprovals:failed_to_fetch_details"),
            });
          }
        } catch (error) {
          Toast({
            type: "error",
            title: t("verificationApprovals:error"),
            message: t("verificationApprovals:failed_to_fetch_details"),
          });
        } finally {
          setIsFetchingApiData(false);
        }
      } else {
        setIsModalOpen(true);
      }
    } else {
      setIsModalOpen(true);
    }
  };

  const handleBackClick = () => {
    navigate("/issuer/approvals/all", {
      state: {
        filters: location.state?.filters,
      },
    });
  };

  const handleConfirmAction = async (actionType, comment = "") => {
    const body = {
      transaction_id: transaction_id,
      verification_approval_status: actionType,
      ...(comment.trim() && { issuer_comments: comment.trim() }),
    };
    try {
      const response = await POST(
        EntityConstants.GET_VERIFICATION_APPROVALS_LIST,
        body,
      );
      if (response.ok) {
        setCurrentStatus(actionType);
        Toast({
          type: "success",
          title: t("verificationApprovals:transaction_updated"),
          message: `${t(
            "verificationApprovals:transaction_has_been",
          )} ${actionType.toLowerCase()} ${t(
            "verificationApprovals:successfully",
          )}!`,
        });

        // Navigate back to the list after a short delay
        setTimeout(() => {
          navigate("/issuer/approvals/all", {
            state: {
              filters: location.state?.filters,
            },
          });
        }, 1500);
      } else {
        Toast({
          type: "error",
          title: t("verificationApprovals:error"),
          message: t("verificationApprovals:there_was_error_updating_status"),
        });
      }
    } catch (error) {
      Toast({
        type: "error",
        title: t("verificationApprovals:error"),
        message: t("verificationApprovals:error_updating_transaction_status"),
      });
    }
    setIsModalOpen(false);
  };

  const handleView = () => {
    if (!transactionDetails?.uploaded_document) {
      Toast({
        type: "error",
        title: t("verificationApprovals:error"),
        message: t("verificationApprovals:certificate_preview_not_available"),
      });
      return;
    }
    setIsOpen(true);
    setCurrentTransactionId(transaction_id);
  };

  const handleViewOriginal = () => {
    if (!transactionDetails?.issued_certificate_svg_url) {
      Toast({
        type: "error",
        title: t("verificationApprovals:error"),
        message: t(
          "verificationApprovals:original_certificate_preview_not_available",
        ),
      });
      return;
    }
    setIsCertificateModalOpen(true);
  };

  const fetchMatchingVCs = async () => {
    setIsMatchingVCsLoading(true);
    try {
      const response = await GET(
        `${EntityConstants.GET_VERIFICATION_APPROVALS_LIST}${transaction_id}/search`,
      );

      if (response.status === 403) {
        setIsUnauthorized(true);
        return;
      }

      const data = await response.json();

      if (data.error) {
        Toast({
          type: "error",
          title: t("verificationApprovals:error"),
          message: data.error,
        });
      } else {
        setMatchingVCs(data);
      }
    } catch (error) {
      Toast({
        type: "error",
        title: t("verificationApprovals:error"),
        message: t("verificationApprovals:failed_load_matching_vcs"),
      });
    } finally {
      setIsMatchingVCsLoading(false);
    }
  };

  const handleDynamicModalClose = (shouldNavigate = false) => {
    stopLlmPolling();
    setDynamicModalData(null);
    setIsDynamicModalOpen(false);

    if (shouldNavigate) {
      setTimeout(() => {
        navigate("/issuer/approvals/all", {
          state: {
            filters: location.state?.filters,
          },
        });
      }, 500);
    }
  };

  useEffect(() => {
    const fetchTransactionDetails = async () => {
      try {
        setLoading(true);
        const response = await GET(
          `${EntityConstants.GET_VERIFICATION_APPROVALS_LIST}${transaction_id}`,
        );

        if (response.status === 403) {
          setIsUnauthorized(true);
          setLoading(false);
          return;
        }

        const data = await response.json();

        if (data.error) {
          Toast({
            type: "error",
            title: t("verificationApprovals:error"),
            message: data.error,
          });
        } else {
          setTransactionDetails(data);

          setCurrentStatus(
            resolveVerificationApprovalStatus(data, location.state),
          );

          setIssuerComments(data?.issuer_comments || "");
          setVerifierComments(data.comments);
          setVerificationReport(data.verification_report_pdf_url);

          if (
            data.document_type === "Legacy" ||
            data.document_type === "Data" ||
            data.document_type === "OCR"
          ) {
            await fetchMatchingVCs();
          }
        }
      } catch (error) {
        Toast({
          type: "error",
          title: t("verificationApprovals:error"),
          message: t("verificationApprovals:failed_load_transaction_details"),
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTransactionDetails();
  }, [transaction_id]);

  useEffect(() => {
    return () => {
      if (llmPollingIntervalRef.current) {
        clearInterval(llmPollingIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const loadUserData = async () => {
      const data = await fetchCurrentUser();
      if (data) {
        setTenantCode(data.tenant_code);
        setCurrentUserOrgCode(data.org_code);
      }
    };
    loadUserData();
  }, []);

  const fetchTransferOrgs = useCallback(async () => {
    try {
      if (!tenantCode) return;
      setIsTransferOrgsLoading(true);
      const data = await fetchOrganisationsAPI(tenantCode, 0, 100);

      const options = data.options.filter((opt) => opt.value !== "");
      setTransferOrgOptions(options);
    } catch (error) {
      console.error("Error fetching transfer orgs:", error);
      Toast({
        type: "error",
        title: t("verificationApprovals:error"),
        message: t("verificationApprovals:failed_fetch_organisations"),
      });
    } finally {
      setIsTransferOrgsLoading(false);
    }
  }, [tenantCode, t]);

  const fetchProgrammes = useCallback(
    async (orgCode) => {
      if (!orgCode) return;
      setIsProgrammesLoading(true);
      setProgrammeOptions([]);
      setSelectedProgramme(null);
      try {
        const response = await GET(
          `${EntityConstants.GET_ISSUER_PROGRAMMES}?code=${orgCode}`,
        );
        const data = await response.json();
        if (data.success && data.data) {
          const options = data.data
            .filter((p) => p.is_active)
            .map((p) => ({
              name: p.programme_display_name || p.programme_name,
              value: p.programme_code,
            }));
          setProgrammeOptions(options);
        } else {
          setProgrammeOptions([]);
          Toast({
            type: "error",
            title: t("verificationApprovals:error"),
            message:
              data.message ||
              t("verificationApprovals:failed_fetch_programmes"),
          });
        }
      } catch (error) {
        console.error("Error fetching programmes:", error);
        Toast({
          type: "error",
          title: t("verificationApprovals:error"),
          message: t("verificationApprovals:error_fetching_programmes"),
        });
      } finally {
        setIsProgrammesLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    if (isTransferDrawerOpen && tenantCode && transferOrgOptions.length === 0) {
      fetchTransferOrgs();
    }
    if (
      isTransferDrawerOpen &&
      transferOrgOptions.length > 0 &&
      currentUserOrgCode &&
      !selectedTransferOrg
    ) {
      const matchingOrg = transferOrgOptions.find(
        (opt) => opt.value === currentUserOrgCode,
      );
      if (matchingOrg) {
        setSelectedTransferOrg(matchingOrg);
        fetchProgrammes(matchingOrg.value);
      }
    }
  }, [
    isTransferDrawerOpen,
    tenantCode,
    fetchTransferOrgs,
    transferOrgOptions.length,
    currentUserOrgCode,
    selectedTransferOrg,
    transferOrgOptions,
    fetchProgrammes,
  ]);

  useEffect(() => {
    if (
      isTransferDrawerOpen &&
      programmeOptions.length > 0 &&
      transactionDetails?.programme_code &&
      !selectedProgramme
    ) {
      const matchingProgramme = programmeOptions.find(
        (opt) => opt.value === transactionDetails.programme_code,
      );
      if (matchingProgramme) {
        setSelectedProgramme(matchingProgramme);
      }
    }
  }, [
    isTransferDrawerOpen,
    programmeOptions,
    transactionDetails?.programme_code,
    selectedProgramme,
  ]);

  const handleTransfer = async () => {
    if (!selectedTransferOrg || !selectedTransferOrg.value) {
      Toast({
        type: "error",
        title: t("verificationApprovals:error"),
        message: t("verificationApprovals:select_organisation_to_transfer"),
      });
      return;
    }

    if (!selectedProgramme || !selectedProgramme.value) {
      Toast({
        type: "error",
        title: t("verificationApprovals:error"),
        message: t("verificationApprovals:select_programme"),
      });
      return;
    }

    setIsTransferSubmitting(true);
    try {
      const response = await POST(
        `${EntityConstants.GET_VERIFICATION_APPROVALS_LIST}${transaction_id}/transfer`,
        {
          issuer_organisation_code: selectedTransferOrg.value,
          programme_code: selectedProgramme.value,
        },
      );

      const responseData = await response.json();

      if (response.ok && responseData.success) {
        Toast({
          type: "success",
          title: t("verificationApprovals:success"),
          message:
            responseData.message ||
            t(
              "verificationApprovals:verification_request_transferred_successfully",
            ),
        });
        setTransferResult(responseData.data);
        setIsTransferred(true);
        setIsTransferDrawerOpen(false);
        setSelectedTransferOrg(null);
        setSelectedProgramme(null);
      } else {
        Toast({
          type: "error",
          title: t("verificationApprovals:error"),
          message:
            responseData.message ||
            t("verificationApprovals:failed_to_transfer_verification_request"),
        });
      }
    } catch (error) {
      console.error("Transfer error:", error);
      Toast({
        type: "error",
        title: t("verificationApprovals:error"),
        message: t("verificationApprovals:error_transferring"),
      });
    } finally {
      setIsTransferSubmitting(false);
    }
  };

  const stablePdfUrl = useMemo(() => {
    return getStablePdfUrl(transactionDetails?.uploaded_document);
  }, [transactionDetails?.uploaded_document]);

  if (isUnauthorized) {
    return (
      <div
        className="min-h-screen bg-cfSecondary pt-20 pb-10"
        id={pageRootTestIds.transactionDetails}
      >
        <div className="w-full bg-cfSearchContainer flex justify-center py-[-0.9rem] px-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between w-full gap-2 sm:gap-0 max-w-8xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center h-[4rem] sm:items-center justify-between w-full">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span
                  onClick={handleBackClick}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleBackClick();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  className="hover:text-gray-800 cursor-pointer"
                >
                  {t("verificationApprovals:verification_approvals")}
                </span>
                <ChevronLeft className="h-4 w-4 text-gray-500 transform rotate-180" />
                <span className="text-gray-800 font-medium">
                  {t("verificationApprovals:transaction_id")}: #{transaction_id}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 mt-20">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-10 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center">
              <ArrowLeftRight className="w-10 h-10 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              {t("verificationApprovals:verification_request_transferred")}
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-8 max-w-md mx-auto">
              {t(
                "verificationApprovals:transferred_request_no_longer_accessible",
              )}
            </p>
            <button
              onClick={handleBackClick}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-[8px] bg-cfPrimaryButton text-white font-medium hover:bg-cfPrimaryDark transition-colors duration-200"
            >
              <ChevronLeft className="w-4 h-4" />
              {t("verificationApprovals:go_back_to_approvals")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading || currentStatus === null) {
    return (
      <div
        className="min-h-screen bg-gray-50 flex items-start justify-center"
        id={pageRootTestIds.transactionDetails}
      >
        <div className="mt-40">
          <CfLoader />
        </div>
      </div>
    );
  }

  const handleViewMatches = () => {
    if (matchingVCs.length === 0) {
      Toast({
        type: "error",
        title: t("verificationApprovals:error"),
        message: t("verificationApprovals:no_matching_certificates_found"),
      });
      return;
    }
    setIsMatchesModalOpen(true);
  };

  const isLegacy = transactionDetails?.document_type?.toLowerCase() !== "vc";

  const labelMap =
    transactionDetails?.verification_form?.components?.reduce(
      (labels, component) => {
        labels[component.key] = component.label;
        return labels;
      },
      {},
    ) || {};

  const downloadPDF = async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(t("verificationApprovals:failed_fetch_file"));
      }

      const disposition = response.headers.get("Content-Disposition");
      let filename = `${transaction_id}_${t("verificationApprovals:verification_report")}.pdf`;

      if (disposition && disposition.includes("attachment")) {
        const matches = /filename="([^"]*)"/.exec(disposition);
        if (matches != null && matches[1]) {
          filename = matches[1];
        }
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
  };

  const downloadVerificationReport = (verificationReport) => {
    if (verificationReport) {
      downloadPDF(verificationReport);
    }
  };

  return (
    <div
      className="min-h-screen bg-cfSecondary pt-20 pb-10"
      id={pageRootTestIds.transactionDetails}
    >
      {/* Header */}
      <div className="w-full bg-cfSearchContainer flex justify-center py-[-0.9rem] px-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between w-full gap-2 sm:gap-0 max-w-8xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
            <div className="flex flex-col sm:flex-row items-center h-[4rem] sm:items-center justify-between w-full">
              <div className="">
                <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg self-start my-auto">
                  <h2 className="text-lg font-medium flex items-center justify-center h-[4rem] w-full">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <span
                        onClick={handleBackClick}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleBackClick();
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        className="hover:text-gray-800 cursor-pointer"
                      >
                        {t("verificationApprovals:verification_approvals")}
                      </span>
                      <ChevronLeft className="h-4 w-4 text-gray-500 transform rotate-180" />
                      <span className="text-gray-800 font-medium">
                        {t("verificationApprovals:transaction_id")}: #
                        {transaction_id}
                      </span>
                    </div>
                  </h2>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-8xl mx-auto px-4 bg-cfSecondary">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center py-4">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl font-bold text-gray-900">
              {t("verificationApprovals:transaction_id")}: #{transaction_id}
            </h1>
            <div className="group flex items-center">
              <button
                onClick={() =>
                  navigate(`/issuer/approvals/${transaction_id}/audit-logs`, {
                    state: location.state,
                  })
                }
                aria-label={t("auditLogs:view_audit_log")}
                className="flex items-center gap-0 group-hover:gap-2 group-focus-within:gap-2 h-[2.5rem] rounded-[8px] bg-cfDisabledLight group-hover:bg-cfPrimaryButton group-focus-within:bg-cfPrimaryButton text-cfPrimary group-hover:text-white group-focus-within:text-white font-semibold text-sm transition-all duration-300 ease-in-out overflow-hidden px-2 group-hover:px-4 group-focus-within:px-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary focus-visible:ring-offset-2"
              >
                <Logs className="w-5 h-4 shrink-0" />
                <span className="max-w-0 group-hover:max-w-[16rem] group-focus-within:max-w-[16rem] overflow-hidden whitespace-nowrap transition-all duration-300 ease-in-out">
                  {t("auditLogs:view_audit_log")}
                </span>
              </button>
            </div>
          </div>

          {isTransferred && transferResult ? (
            <div className="w-full lg:w-auto">
              <div className="flex items-center gap-3 px-4 py-3 rounded-[8px] border-none bg-emerald-100 text-cfActiveText">
                <CheckCircle2 className="w-6 h-6 text-cfActiveText shrink-0" />
                <span className="text-base font-semibold">
                  {t(
                    "verificationApprovals:verification_transferred_successfully",
                  )}
                </span>
              </div>
            </div>
          ) : currentStatus === "Approval Pending" ? (
            <div className="flex flex-col items-start space-y-4 lg:flex-row lg:space-y-0 lg:space-x-4">
              <button
                onClick={handleAccept}
                disabled={isFetchingApiData}
                className="flex rounded-[8px] items-center justify-center gap-2 w-full lg:w-[10rem] h-[2.5rem] bg-cfPrimaryButton text-white font-medium lg:font-semibold text-sm lg:text-md hover:bg-cfPrimaryDark transition duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isFetchingApiData
                  ? t("verificationApprovals:loading")
                  : t("verificationApprovals:approve")}
              </button>
              <button
                onClick={handleReject}
                disabled={isFetchingApiData}
                className="flex rounded-[8px] items-center justify-center gap-2 w-full lg:w-[10rem] h-[2.5rem] bg-white text-black font-medium lg:font-semibold text-sm lg:text-md border border-gray-800 hover:!bg-gray-100 transition duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isFetchingApiData
                  ? t("verificationApprovals:loading")
                  : t("verificationApprovals:reject")}
              </button>
              {isTransferEnabled && (
                <>
                  <div className="w-[1px] h-[2.5rem] bg-gray-300 hidden lg:!block"></div>
                  <button
                    onClick={() => setIsTransferDrawerOpen(true)}
                    className="flex rounded-[8px] items-center justify-center gap-2 w-full lg:w-[10rem] h-[2.5rem] bg-cfPrimaryButton text-white font-medium lg:font-semibold text-sm lg:text-md hover:bg-cfPrimaryDark transition duration-200 focus:outline-none"
                  >
                    {t("verificationApprovals:transfer")}
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <div
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium  ${
                  currentStatus === "Approved"
                    ? "border-emerald-300 bg-emerald-100 text-cfActiveText"
                    : "border-red-300 text-cfIssuedCredentialTemplateCardStatusFailedBG bg-red-200"
                }`}
              >
                {currentStatus === "Approved" ? (
                  <CheckCircle2 className="w-5 h-5 text-cfActiveText shrink-0" />
                ) : (
                  <XCircle className="w-5 h-4 text-cfIssuedCredentialTemplateCardStatusFailedBG shrink-0" />
                )}
                <span className="font-semibold">
                  {currentStatus === "Approved"
                    ? t("verificationApprovals:approved")
                    : t("verificationApprovals:rejected")}
                </span>
              </div>

              {verificationReport && (
                <button
                  className="px-4 py-2 rounded-lg bg-cfPrimaryButton text-white font-medium"
                  onClick={() => downloadVerificationReport(verificationReport)}
                >
                  {t("verificationApprovals:download_report")}
                </button>
              )}

              {issuerComments && (
                <div className="relative group">
                  <img
                    src={AlertIcon}
                    alt="Info"
                    className="w-5 h-5 cursor-pointer"
                  />
                  <div className="absolute right-[-3rem] top-8 w-72 scale-0 transform rounded-lg text-xs font-medium transition-all duration-500 group-hover:scale-100 z-50">
                    <div className="flex flex-col items-center">
                      <div className="rounded-lg px-3 py-3 text-xs border border-gray-900 bg-white text-black whitespace-normal break-words">
                        <div className="flex flex-col h-full">
                          <span className="font-bold text-[16px] text-gray-900">
                            {currentStatus === "Approved"
                              ? t("verificationApprovals:approval_comment")
                              : t("verificationApprovals:rejection_reason")}
                          </span>
                          <span className="mt-1 text-[14px] text-gray-600 flex-grow">
                            {issuerComments ||
                              t("verificationApprovals:no_comment_provided")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {isTransferred && transferResult && (
          <div className="rounded-xl border-none bg-gradient-to-r from-blue-50 to-indigo-50 p-6 mb-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <ArrowLeftRight className="w-5 h-5 text-cfPrimary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t(
                    "verificationApprovals:verification_transferred_successfully",
                  )}
                </h3>
                <p className="text-sm text-gray-500">
                  {t("verificationApprovals:request_pending_after_transfer")}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-6 border border-blue-100 shadow-sm">
                {transferResult.previous_issuer_organisation_code !==
                  transferResult.new_issuer_organisation_code && (
                  <>
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                      {t("verificationApprovals:from_organisation")}
                    </span>
                    <p className="text-sm font-semibold text-gray-800 mt-0.5">
                      {transferResult.previous_issuer_organisation_name
                        ? transferResult.previous_issuer_organisation_name
                        : t("verificationApprovals:unavailable")}
                    </p>
                    <div className="my-4 border-t border-gray-200" />
                  </>
                )}
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                  {t("verificationApprovals:from_programme")}
                </span>
                <p className="text-sm font-semibold text-gray-800 mt-0.5">
                  {transferResult.previous_programme_name
                    ? transferResult.previous_programme_name
                    : t("verificationApprovals:unavailable")}
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-blue-100 shadow-sm">
                {transferResult.previous_issuer_organisation_code !==
                  transferResult.new_issuer_organisation_code && (
                  <>
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                      {t("verificationApprovals:to_organisation")}
                    </span>
                    <p className="text-sm font-semibold text-cfPrimary mt-0.5">
                      {transferResult.new_issuer_organisation_name}
                    </p>
                    <div className="my-4 border-t border-gray-200" />
                  </>
                )}
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                  {t("verificationApprovals:to_programme")}
                </span>
                <p className="text-sm font-semibold text-cfPrimary mt-0.5">
                  {transferResult.new_programme_name}
                </p>
              </div>
            </div>
          </div>
        )}

        {(transactionDetails?.vc_status === "Verified" ||
          transactionDetails?.vc_status === "Revoked") &&
          transactionDetails?.issued_certificate_svg_url && (
            <div
              className={`flex flex-col lg:flex-row items-center justify-between w-full px-4 py-3 rounded-lg font-semibold text-[24px] mb-4 shadow-md border-none gap-4 ${
                transactionDetails.vc_status === "Verified"
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              <div className="w-full lg:w-2/3 flex items-center justify-center gap-3 text-center">
                {transactionDetails.vc_status === "Verified" && (
                  <div className="w-12 h-12 flex-shrink-0">
                    <Lottie
                      animationData={verificationAnimation}
                      loop={true}
                      autoplay={true}
                      style={{ width: "100%", height: "100%" }}
                    />
                  </div>
                )}
                <span className="text-[24px] font-medium lg:font-semibold">
                  {transactionDetails.vc_status === "Verified"
                    ? t("verificationApprovals:credential_verified_valid")
                    : t("verificationApprovals:credential_revoked_invalid")}
                </span>
              </div>

              <div className="w-full lg:w-1/3 flex justify-center lg:justify-center">
                <button
                  onClick={handleViewOriginal}
                  className="flex rounded-[8px] items-center justify-center gap-2 w-full lg:w-[10rem] h-[2.5rem] bg-white text-black font-medium lg:font-semibold text-sm lg:text-md border border-gray-800 hover:bg-gray-100 transition duration-200 focus:outline-none"
                >
                  {t("verificationApprovals:view_original")}
                </button>
              </div>
            </div>
          )}

        {!loading && isLegacy && !isMatchingVCsLoading && (
          <div
            className={`flex flex-col lg:flex-row items-center justify-between w-full px-4 py-3 rounded-lg font-semibold text-[24px] mb-4 shadow-md border-none gap-4 ${
              matchingVCs.length > 0
                ? "bg-green-100 text-green-800"
                : "bg-[#fef2c6] text-[#db6a02]"
            }`}
          >
            {matchingVCs.length > 0 ? (
              <>
                <div className="w-full lg:w-2/3 flex items-center justify-center lg:justify-center gap-3 text-center">
                  <div className="w-12 h-12 flex-shrink-0"></div>
                  <span className="text-[24px] font-medium lg:font-semibold">
                    {`${matchingVCs.length} ${
                      matchingVCs.length > 1
                        ? `${t("verificationApprovals:matches")}`
                        : `${t("verificationApprovals:match")}`
                    } ${t("verificationApprovals:found_for_certificate")}`}
                  </span>
                </div>
                <div className="w-full lg:w-1/3 flex justify-center lg:justify-center">
                  <button
                    onClick={handleViewMatches}
                    className="flex rounded-[8px] items-center justify-center gap-2 w-full lg:w-[10rem] h-[2.5rem] bg-white text-black font-medium lg:font-semibold text-sm lg:text-md border border-gray-800 hover:bg-gray-100 transition duration-200"
                  >
                    {t("verificationApprovals:view_certificates")}
                  </button>
                </div>
              </>
            ) : (
              <div className="w-full flex items-center justify-center gap-3 text-center">
                <span className="text-[24px] font-medium lg:font-semibold">
                  {t(
                    "verificationApprovals:no_matching_certificates_found_exclamation",
                  )}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Candidate Details Card */}
            <div className="bg-white rounded-lg shadow-md border border-[#fbfbfb] p-6 max-h-screen overflow-y-auto">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 sticky top-0 bg-white py-2">
                {t("verificationApprovals:candidate_details")}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {isLegacy ? (
                  // Dynamic Legacy Fields using form structure builder
                  transactionDetails?.verification_data &&
                  Object.keys(transactionDetails.verification_data).length >
                    0 ? (
                    <>
                      {renderSimpleFields(verificationDataStructure, labelMap)}

                      {renderComplexFields(verificationDataStructure, labelMap)}
                    </>
                  ) : (
                    <p className="text-gray-500 col-span-2">
                      {t(
                        "verificationApprovals:no_candidate_details_available",
                      )}
                    </p>
                  )
                ) : (
                  // VC Fields
                  <>
                    <div className="space-y-4">
                      <div>
                        <span className="text-sm text-gray-500 block">
                          {t("verificationApprovals:name")}
                        </span>
                        <p className="text-gray-900">
                          {transactionDetails?.holder_name ||
                            t("verificationApprovals:na")}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500 block">
                          {t("verificationApprovals:certificate_id")}
                        </span>
                        <p className="text-gray-900">
                          {transactionDetails?.certificate_id ||
                            t("verificationApprovals:na")}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500 block">
                          {t("verificationApprovals:phone")}
                        </span>
                        <p className="text-gray-900">
                          {transactionDetails?.holder_phone_number ||
                            t("verificationApprovals:na")}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <span className="text-sm text-gray-500 block">
                          {t("verificationApprovals:programme_name")}
                        </span>
                        <p className="text-gray-900">
                          {transactionDetails?.programme_name ||
                            t("verificationApprovals:na")}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500 block">
                          {t("verificationApprovals:email")}
                        </span>
                        <p className="text-gray-900">
                          {transactionDetails?.holder_email ||
                            t("verificationApprovals:na")}
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Verifier Organisation Details Card */}
            <div className="bg-white rounded-lg shadow-md border border-[#fbfbfb] p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t("verificationApprovals:verifier_organisation_details")}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <span className="text-sm text-gray-500 block">
                      {t("verificationApprovals:verifier_organisation")}
                    </span>
                    <p className="text-gray-900">
                      {transactionDetails?.verifier_org_name ||
                        t("verificationApprovals:na")}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 block">
                      {t("verificationApprovals:organisation_code")}
                    </span>
                    <p className="text-gray-900">
                      {transactionDetails?.verifier_org_code ||
                        t("verificationApprovals:na")}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 block">
                      {t("verificationApprovals:verifier_email")}
                    </span>
                    <p className="text-gray-900">
                      {transactionDetails?.verifier_email ||
                        t("verificationApprovals:na")}
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <span className="text-sm text-gray-500 block">
                      {t("verificationApprovals:created_at")}
                    </span>
                    <p className="text-gray-900">
                      {formatDate(transactionDetails?.created_at)}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 block">
                      {t("verificationApprovals:verifier_name")}
                    </span>
                    <p className="text-gray-900">
                      {transactionDetails?.verifier_name ||
                        t("verificationApprovals:na")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Summary Card */}
            {((transactionDetails?.verification_cost &&
              parseFloat(transactionDetails?.verification_cost) !== 0) ||
              transactionDetails?.payment_date ||
              transactionDetails?.payment_id ||
              transactionDetails?.mode_of_payment) && (
              <div className="bg-white rounded-lg shadow-md border border-[#fbfbfb] p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {t("verificationApprovals:payment_summary")}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <span className="text-sm text-gray-500 block">
                        {t("verificationApprovals:amount_paid")}
                      </span>
                      <p className="text-gray-900">
                        {transactionDetails?.verification_cost &&
                        parseFloat(transactionDetails?.verification_cost) !== 0
                          ? `₹ ${(
                              parseFloat(
                                transactionDetails?.verification_cost,
                              ) / 100
                            ).toFixed(2)}`
                          : t("verificationApprovals:na")}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500 block">
                        {t("verificationApprovals:paid_at")}
                      </span>
                      <p className="text-gray-900">
                        {transactionDetails?.payment_date
                          ? formatDate(transactionDetails?.payment_date)
                          : t("verificationApprovals:na")}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500 block">
                        {t("verificationApprovals:payment_status")}
                      </span>
                      <p className="text-gray-900">
                        {transactionDetails?.payment_status === "Done"
                          ? t("verificationApprovals:paid")
                          : transactionDetails?.payment_status ||
                            t("verificationApprovals:free_of_charge")}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <span className="text-sm text-gray-500 block">
                        {t("verificationApprovals:reference_id")}
                      </span>
                      <p className="text-gray-900">
                        {transactionDetails?.payment_id ||
                          t("verificationApprovals:na")}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500 block">
                        {t("verificationApprovals:mode_of_payment")}
                      </span>
                      <p className="text-gray-900">
                        {transactionDetails?.mode_of_payment ||
                          t("verificationApprovals:na")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Document Preview */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md border border-[#fbfbfb] p-6 sticky top-24">
              {verifierComments && (
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    {t("verificationApprovals:verifier_comments")}
                  </h2>
                  <textarea
                    placeholder={t("verificationApprovals:comments")}
                    className="border-2 border-dashed border-gray-200 rounded-lg p-2 w-full text-sm placeholder:text-sm resize-none overflow-y-auto"
                    name="verifier_comments"
                    value={verifierComments}
                    disabled
                    rows={4}
                    style={{ maxHeight: "7.5rem" }}
                  ></textarea>
                </div>
              )}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {t("verificationApprovals:verification_document")}
                </h2>
                <div className="space-y-4">
                  <div>
                    <span className="text-sm text-gray-500 block">
                      {t("verificationApprovals:document_type")}
                    </span>
                    <p className="text-gray-900">
                      {transactionDetails?.document_type ||
                        t("verificationApprovals:na")}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 block">
                      {t("verificationApprovals:verification_mode")}
                    </span>
                    <p className="text-gray-900">
                      {transactionDetails?.mode_of_verification ||
                        t("verificationApprovals:na")}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 block">
                      {t("verificationApprovals:uploaded_at")}
                    </span>
                    <p className="text-gray-900">
                      {formatDate(transactionDetails?.uploaded_date)}
                    </p>
                  </div>

                  <div className="mt-6">
                    <span className="text-sm text-gray-500 block mb-3">
                      {t("verificationApprovals:document_preview")}
                    </span>
                    <button
                      className="w-full aspect-square rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center mb-6 relative focus:outline-none"
                      onClick={handleView}
                      onKeyDown={handleView}
                      aria-label="View document preview"
                    >
                      {transactionDetails?.uploaded_document ? (
                        <>
                          {(() => {
                            const uploadedFileType = detectFileType(
                              transactionDetails.uploaded_document,
                            );

                            if (uploadedFileType === "image") {
                              return (
                                <img
                                  src={transactionDetails.uploaded_document}
                                  alt="Document Preview"
                                  className="max-w-full max-h-full object-contain"
                                />
                              );
                            } else if (uploadedFileType === "pdf") {
                              return (
                                <div className="w-full h-full flex items-center justify-center">
                                  <PdfPreview pdfUrl={stablePdfUrl} />
                                </div>
                              );
                            } else {
                              return (
                                <p className="text-gray-400 text-center text-sm p-4">
                                  {t(
                                    "verificationApprovals:preview_not_available_click",
                                  )}
                                </p>
                              );
                            }
                          })()}
                        </>
                      ) : (
                        <p className="text-gray-400">
                          {t("verificationApprovals:no_preview_available")}
                        </p>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <VerificationApprovalModal
        isModalOpen={isModalOpen}
        toggleModal={setIsModalOpen}
        actionType={actionType}
        handleConfirmAction={() =>
          handleConfirmAction(actionType, issuerComments)
        }
        issuerComments={issuerComments}
        setIssuerComments={setIssuerComments}
        transaction_id={transaction_id}
        isLegacy={isLegacy}
        matchingVCs={matchingVCs}
        vcStatus={transactionDetails?.vc_status}
      />

      {isDynamicModalOpen && dynamicModalData && (
        <DynamicVerificationApprovalModal
          isModalOpen={isDynamicModalOpen}
          toggleModal={setIsDynamicModalOpen}
          actionType={actionType}
          transaction_id={transaction_id}
          apiData={dynamicModalData.llmReportData}
          defaultFields={dynamicModalData.defaultFields}
          onClose={handleDynamicModalClose}
          isLlmPolling={isLlmPolling}
        />
      )}

      {isOpen && currentTransactionId === transaction_id && (
        <VerificationApprovalCertificateModal
          isOpen={isOpen}
          documentType={transactionDetails?.document_type}
          uploadedCertificateImage={transactionDetails?.uploaded_document}
          certificateData={
            transactionDetails.issued_certificate_details &&
            Object.keys(transactionDetails.issued_certificate_details).length >
              0
              ? transactionDetails.issued_certificate_details
              : transactionDetails.verification_data
          }
          openCloseModal={() => setIsOpen(false)}
          certificateId={transactionDetails?.certificate_id}
        />
      )}

      {isCertificateModalOpen && (
        <VerificationApprovalCertificateModal
          isOpen={isCertificateModalOpen}
          documentType={transactionDetails?.document_type}
          uploadedCertificateImage={
            transactionDetails?.issued_certificate_svg_url
          }
          certificateData={
            transactionDetails.issued_certificate_details &&
            Object.keys(transactionDetails.issued_certificate_details).length >
              0
              ? transactionDetails.issued_certificate_details
              : transactionDetails.verification_data
          }
          openCloseModal={() => setIsCertificateModalOpen(false)}
          certificateId={transactionDetails?.certificate_id}
        />
      )}

      {isMatchesModalOpen && (
        <LegacyMatchesModal
          isOpen={isMatchesModalOpen}
          certificateData={matchingVCs.map((vc) => vc.svg_url)}
          openCloseModal={() => setIsMatchesModalOpen(false)}
          handleConfirmAction={handleConfirmAction}
          transactionId={transaction_id}
          matchingVCs={matchingVCs}
          currentStatus={currentStatus}
        />
      )}

      <SideDrawer
        isOpen={isTransferDrawerOpen}
        onClose={() => setIsTransferDrawerOpen(false)}
        position="right"
        title={t("verificationApprovals:transfer_verification")}
        width="420px"
        headerIcon={<Info className="w-5 h-5" />}
        testId="transaction-details-transfer-drawer"
      >
        <div className="flex flex-col h-full justify-between">
          <div className="space-y-6">
            <p className="text-sm text-gray-500">
              {t("verificationApprovals:transfer_request_description")}
            </p>

            <div className="space-y-2">
              <span className="block text-sm font-medium text-gray-700">
                {t("verificationApprovals:select_organisation")}{" "}
                <span className="text-red-500">*</span>
              </span>
              {isTransferOrgsLoading ? (
                <div className="flex justify-center p-4">
                  <SmallCFLoader />
                </div>
              ) : (
                <Dropdown
                  key={selectedTransferOrg?.value || "org-placeholder"}
                  options={transferOrgOptions}
                  selected={
                    selectedTransferOrg || {
                      name: t("verificationApprovals:select_organisation"),
                      value: "",
                    }
                  }
                  onSelect={(value) => {
                    if (!value) return;
                    if (value === selectedTransferOrg?.value) return;
                    const selected = transferOrgOptions.find(
                      (opt) => opt.value === value,
                    );
                    console.log(selected);
                    setSelectedTransferOrg(selected);
                    if (selected && selected.value) {
                      fetchProgrammes(selected.value);
                    } else {
                      setProgrammeOptions([]);
                      setSelectedProgramme(null);
                    }
                  }}
                  buttonClass="w-full border-gray-300 shadow-sm"
                  menuClass="w-full max-h-60"
                  isSearchable={true}
                  testId="transaction-details-dropdown-transfer-org"
                />
              )}
            </div>

            <div className="space-y-2">
              <span
                className={`block text-sm font-medium ${selectedTransferOrg && selectedTransferOrg.value ? "text-gray-700" : "text-gray-400"}`}
              >
                {t("verificationApprovals:select_programme_label")}{" "}
                <span className="text-red-500">*</span>
              </span>
              {isProgrammesLoading ? (
                <div className="flex justify-center p-4">
                  <SmallCFLoader />
                </div>
              ) : (
                <div
                  className={
                    !selectedTransferOrg || !selectedTransferOrg.value
                      ? "opacity-50 pointer-events-none"
                      : ""
                  }
                >
                  <Dropdown
                    key={selectedProgramme?.value || "prog-placeholder"}
                    options={programmeOptions}
                    selected={
                      selectedProgramme || {
                        name:
                          selectedTransferOrg && selectedTransferOrg.value
                            ? t("verificationApprovals:select_programme_label")
                            : t(
                                "verificationApprovals:select_organisation_first",
                              ),
                        value: "",
                      }
                    }
                    onSelect={(value) => {
                      if (!value) return;
                      if (value === selectedProgramme?.value) return;
                      const selected = programmeOptions.find(
                        (opt) => opt.value === value,
                      );
                      setSelectedProgramme(selected);
                    }}
                    buttonClass="w-full border-gray-300 shadow-sm"
                    menuClass="w-full max-h-60"
                    isSearchable={true}
                    testId="transaction-details-dropdown-transfer-programme"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-3 pt-4">
            <button
              onClick={handleTransfer}
              disabled={
                isTransferSubmitting ||
                !selectedTransferOrg ||
                !selectedProgramme
              }
              className="w-full flex items-center justify-center py-2.5 px-4 rounded-[8px] bg-cfPrimaryButton text-white font-medium hover:bg-cfPrimaryDark disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isTransferSubmitting ? (
                <SmallCFLoader className="w-4 h-4 text-white" />
              ) : (
                t("verificationApprovals:transfer_request")
              )}
            </button>
            <button
              onClick={() => setIsTransferDrawerOpen(false)}
              disabled={isTransferSubmitting}
              className="w-full py-2.5 px-4 rounded-[8px] border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors duration-200"
            >
              {t("verificationApprovals:cancel")}
            </button>
          </div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default TransactionDetailsPage;
